/**
 * Composer 동적 데이터셋 행 편집 그리드 (범용 컴포넌트).
 *
 * DataSourceRowsNode(편집형 list 선언의 합성 노드)의 fields 선언대로 행별 필드를 편집한다.
 * - 편집본 = content[bindingKey] = RowObject[]  / seed 원본 = content[bindingKey + __seed]
 * - 필드 위젯: text / textarea / textarea-list(문자열 배열) / text-list(한 줄 목록) / number /
 *   date(yyyy-MM-dd) / date-range({start,end}) / readonly / registered
 * - widget:'registered' = host가 주입하는 등록형 위젯 맵(registeredWidgets[widgetKey] — 정본
 *   레지스트리 COMPOSED_ROW_WIDGETS). 등록된 위젯은 editable:false여도 항상 위젯이 렌더
 *   (표시 전용 모드는 위젯 책임 — 원시 String 폴백의 [object Object] 차단). 미등록 키는
 *   무해 표시(객체 값은 '-') + 안내 title.
 * - 행 추가(rowId=uuid)/삭제는 별개 축 — shared `effectiveRowAddRemove(node)`(allowAddRemove 상위
 *   스위치 + allowAdd/allowRemove). editable:false 필드는 읽기 전용.
 * - AI 원본 되돌리기: seed에서 같은 rowId 행을 찾아 필드가 달라졌으면 ⟲ 버튼(단일 필드 revert).
 *   seed에 없는 행(수동 추가)은 revert 없음.
 *
 * 순수 상태 변환은 dataSourceCollection.ts(단위 테스트)로 분리했다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentType, CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { ComposerSuggestionRow, DataSourceDef } from '@safetyhub/shared';
// 행 규약 오버레이 키 판정 — 본문 image 노드(RenderTemplate)와 같은 shared 규칙을 행 스코프로 쓴다.
import {
  OVERLAY_SHAPES_CONTENT_KEY,
  OVERLAY_STROKES_CONTENT_KEY,
  effectiveRowAddRemove,
  resolveOverlayAxisVisible,
} from '@safetyhub/shared';
// 범용 검색 제안 축(suggestionsFrom) 드롭다운 — 항목 입력에 render-prop으로 배선.
// suggestionDropdown은 registeredWidgetWrites·RenderTemplate·shared만 import(순환 없음).
import {
  SUGGEST_REQUIRE_SELECTION_PLACEHOLDER,
  SUGGEST_REQUIRE_SELECTION_UNAVAILABLE,
  SUGGEST_UNAVAILABLE_CONTROL_STYLE,
  SuggestNote,
  SuggestionDropdown,
  isTypedCommitBlocked,
  type SuggestionControlBindings,
} from './suggestionDropdown';
// 긴 텍스트 접기(collapseLines) 표시 — 순수 판정 + 토글. shared만 의존(순환 없음).
import { CollapsibleText } from './collapsibleText';
import {
  type DataSourceRowField,
  type DataSourceRowsNode,
  type RowObject,
  type RowScopedPending,
  addListItem,
  addRow,
  collapsedAfterRemove,
  collapsedForToggleAll,
  commitRowScopedRows,
  dataSourceToRowsNode,
  findSeedRow,
  isAllRowsCollapsed,
  isFieldModified,
  isRowAddBlockedByLimit,
  listAfterEditEnd,
  listBeforeAddItem,
  normalizeList,
  normalizeRows,
  reconcileRowScopedPending,
  removeListItem,
  removeRow,
  rowCollapseKey,
  rowVisibleFields,
  summaryFieldsForRow,
  summaryText,
  toggleCollapseKey,
  rowAddLimitTitle,
  updateListItem,
  updateRowField,
} from './dataSourceCollection';
import { RowImageField } from './RowImageField';
import { isAnnotatedImageValue } from './AnnotatedImageView';
import { addItemButtonStyle, addItemButtonDisabledStyle } from './addItemButton';
// 사진 업로드 위젯 한 벌(트리 photo 위젯·DataDefField photo 셀과 같은 컴포넌트 — 사본 금지).
import { ImageWithFallback } from './ImageWithFallback';
import { PhotoUploadWidget, PHOTO_CELL_THUMB_STYLE } from './photoUploadWidget';
import type { ComposerPhotoUpload } from './photoUploadWidget';
// 날짜/기간 컨트롤 — 작성폼 트리 위젯(widget:'date'|'date-range')과 **같은 컴포넌트**를 쓴다.
import { DateFieldInput, DateRangeFieldInput } from './DateFieldInput';
import { dateWidgetDisplayText } from './dateValue';
import {
  SignatureSlotView,
  rowSignerName,
  signatureSlotViewProps,
  type ComposerSignatureSlotsContext,
  type SignatureSlotViewProps,
} from './SignatureSlotView';
// 행 추가 직후 "방금 만든 행"의 첫 입력 칸으로 커서 — 항목 목록(focusOnMount)과 같은 규약을
// 행 단위로 적용한다. 필드 컨트롤은 위젯별 분기 렌더(RowFieldControl)라 ref를 꿸 자리가 없어
// DOM 앵커로 찾는다(addedRowFocus doc).
import {
  LIST_ITEM_INDEX_ATTR,
  firstTypeableCellKey,
  focusAddedGridField,
  focusAddedRowContainer,
} from './addedRowFocus';

/**
 * host가 주입하는 등록형 행 필드 위젯. 도메인 위젯(risk-estimation 등)은 이 계약으로 주입한다.
 * value=행의 해당 field 값, onChange=그 값 갱신, editable=편집 가능 여부.
 * context=host가 주입하는 작성 화면 컨텍스트(rowWidgetContext) — 그리드는 불투명 통로,
 * 해석은 각 위젯의 도메인 책임(미주입 시 undefined — 위젯은 표시 전용 등으로 degrade).
 */
export type RegisteredRowWidget = ComponentType<{
  value: unknown;
  onChange: (next: unknown) => void;
  editable: boolean;
  context?: unknown;
}>;

interface RowsCollectionEditorProps {
  node: DataSourceRowsNode;
  /** 편집본 행 배열(=values[bindingKey]). */
  value: unknown;
  /** seed 원본 행 배열(=values[bindingKey + __seed]) — 되돌리기 대조. */
  seed: unknown;
  /** 행 배열 갱신(호출부가 content[bindingKey]에 반영). */
  onChange: (nextRows: RowObject[]) => void;
  /** widget:'registered' 위젯 맵(widgetKey → 컴포넌트). 미지정=미등록 처리. */
  registeredWidgets?: Record<string, RegisteredRowWidget>;
  /**
   * host가 주입하는 작성 화면 컨텍스트(예 { values, resolvedRows }) — registered 위젯의
   * context prop으로 그대로 전달. 그리드는 불투명 통로(해석은 각 위젯의 도메인 책임).
   */
  rowWidgetContext?: unknown;
  /**
   * 서명 슬롯 host 채널(P2) — 걷은 서명 맵 + 탭/지우기 콜백. 미주입(캔버스·external·생성
   * 모드)이면 표시 전용 빈 칸으로 degrade(모양 동일 — WYSIWYG). slotKey 해소·값 조회는
   * `resolveSlotSignatureDisplay` 단일 지점.
   */
  signatureSlots?: ComposerSignatureSlotsContext;
  /** true면 전 필드 읽기 전용 + 행 추가/삭제·되돌리기 숨김. */
  readOnly?: boolean;
  /**
   * admin 저작 캔버스에서 렌더 중임(라이브 모드 — web 실폼과 같은 컴포넌트를 그대로 그린다).
   * 지금 쓰는 곳은 사진 칸의 클릭 확대 하나다: 모양은 실폼과 같게 두고 **모달만 열리지 않는다**
   * (캔버스에서 모달이 뜨면 트리·속성 패널을 덮어 저작이 끊긴다 — photo 업로드 버튼의
   * `inert = authoringCanvas === true` 선례와 같은 축). 다른 입력은 캔버스에서도 live다.
   */
  authoringCanvas?: boolean;
  /**
   * widget='photo' 필드 업로드 호스트 콜백(트리 photo 위젯 onPhotoUpload와 같은 채널) —
   * 미배선(캔버스·external)이면 photo 필드는 썸네일/안내로 degrade.
   */
  uploadPhoto?: ComposerPhotoUpload;
}

const cellInputClass =
  'w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500';
const fieldLabelClass = 'block text-xs font-medium text-gray-600 mb-1';
const readonlyValueClass =
  'w-full px-2 py-1.5 border border-gray-200 bg-gray-50 rounded text-sm text-gray-500 whitespace-pre-wrap break-words';
// min-h-[2rem]·bg-gray-50/40·space-y-2.5는 admin CSS에 없던 유틸 — rc Tailwind 규약(임계 스타일
// 인라인 소유)에 따라 style로 부여해 캔버스↔실폼 무스타일 갈림을 차단한다.
const readonlyValueStyle: CSSProperties = { minHeight: '2rem' };
// 행 카드: 요약 헤더 + 펼침 편집 영역을 세로로 쌓는다. overflow:hidden으로 헤더 배경이 둥근
// 모서리를 침범하지 않게 한다(색·배경은 rc Tailwind 규약에 따라 인라인 소유).
// export: 반복 위젯(repeat) 컨테이너가 행 카드 골격을 동일 스타일로 승계(중복 정의 금지 —
// 결정 2026-07-20-composer-repeat-widget-row-scope §골격 승계).
export const rowCardStyle: CSSProperties = {
  backgroundColor: 'rgba(249, 250, 251, 0.4)',
  overflow: 'hidden',
};
// 펼침 편집 영역: 구 rowCardStyle의 flex-col gap을 그대로 이어받는다.
const rowBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.625rem',
};
// 요약 헤더(구 SMS 결과 행 접기의 ink-50 헤더 동형). border-bottom은 펼침 시에만.
export const rowHeaderBaseStyle: CSSProperties = {
  backgroundColor: '#f8fafc',
  cursor: 'pointer',
};
// 번호 배지(구 SMS: ink-500 배경 흰 글자 원형).
export const rowBadgeStyle: CSSProperties = {
  backgroundColor: '#64748b',
  color: '#ffffff',
  width: '1.25rem',
  height: '1.25rem',
  borderRadius: '9999px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
// 요약 텍스트 truncate — 잘림(임계 레이아웃)이라 인라인으로 소유.
export const summaryPrimaryStyle: CSSProperties = {
  flex: '1 1 0%',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
export const summarySecondaryStyle: CSSProperties = {
  maxWidth: '12rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
// "+ 항목 추가" 버튼 결은 leaf 모듈(addItemButton.ts)이 정본 — PhotoUploadWidget 분리(2026-09-07)
// 시 순환 회피로 내렸다. 기존 import 경로(이 파일·트리 렌더러) 호환을 위한 re-export.
export { addItemButtonStyle, addItemButtonDisabledStyle } from './addItemButton';

/**
 * 원본으로 되돌리기 버튼(⟲) — RiskAssessmentStep 패턴 준용.
 * export: 반복 위젯(repeat)의 행 단위 되돌리기가 동일 버튼을 승계.
 */
export function RevertButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-colors"
      title="AI 원본으로 되돌리기"
      aria-label="AI 원본으로 되돌리기"
    >
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4"
        />
      </svg>
    </button>
  );
}

/** readonly/미편집 필드의 값 표시 텍스트. */
function readonlyText(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) {
    const items = normalizeList(value).filter((s) => s.trim() !== '');
    return items.length > 0 ? items.join('\n') : '—';
  }
  return String(value);
}

/**
 * 위젯 어휘를 아는 readonly 표시 — 날짜 계열만 먼저 가로채고 나머지는 종전 표기(readonlyText).
 *
 * 값만 보고는 정할 수 없다: 'date'의 값은 평범한 문자열이라 String()으로도 "보이긴" 하지만
 * ISO datetime prefill이 그대로 새고, 'date-range'는 객체라 [object Object]가 된다.
 * 이 그리드의 편집 컨트롤이 한글로 보여 주므로 읽기 전용도 한글로 맞춘다(같은 칸이 편집 가능
 * 여부에 따라 다른 글자로 보이지 않게).
 */
function readonlyFieldText(field: DataSourceRowField, value: unknown): string {
  const dateText = dateWidgetDisplayText(field.widget, value);
  if (dateText !== undefined) return dateText === '' ? '—' : dateText;
  return readonlyText(value);
}

/**
 * 문자열 배열(string[]) 편집 위젯 — 항목 추가/삭제/수정. `multiline`으로 각 항목 입력을 분기:
 *  - `multiline=true`(기본): 각 항목 여러 줄 `<textarea rows={2}>` — textarea-list.
 *  - `multiline=false`: 각 항목 한 줄 `<input type="text">` — text-list(2026-07-20 편입).
 * 값 형상·항목 조작(add/remove/update)·정규화·빈 목록 문구·읽기전용 표시는 두 모드 공용.
 * export: renderWriteNode(composerWidgetTreeRender)의 InputWidget `widget:'textarea-list'`/`'text-list'`
 * 케이스가 같은 컴포넌트를 재사용한다(항목 편집 UI 단일 정본, 중복 구현 금지).
 *
 * entryMode='click'(InputWidget.editEntry — 트리 경로에서만 전달, 행 그리드는 항상 always):
 * 항목이 평소 민짜 텍스트 줄(구 SI 작성 카드 결)로 표시되고, 클릭한 항목만 입력으로 전환된다
 * (blur/Esc 표시 복귀 — 값은 onChange 즉시 전파라 롤백 없음, ClickToEditField 규약 동형).
 * 항목 추가 시 새 항목이 바로 편집으로 열린다(빈 민짜 줄은 클릭 대상이 안 보이므로).
 * 삭제 ×·"+ 항목 추가"는 always와 동일 노출(구 EditableMeasureList도 항목별 삭제 상시 노출).
 */
export function TextareaListField({
  value,
  editable,
  onChange,
  multiline = true,
  entryMode = 'always',
  collapseLines,
  suggest,
  suggestUnavailable = false,
  authoringCanvas = false,
}: {
  value: unknown;
  editable: boolean;
  onChange: (next: string[]) => void;
  multiline?: boolean;
  entryMode?: 'always' | 'click';
  /**
   * 긴 텍스트 접기(InputWidget.collapseLines — 트리 경로에서만 전달, 행 그리드는 미배선):
   * **항목 단위 표시 상태**에만 적용한다(entryMode='click'의 민짜 줄). 편집 중인 항목은 컨트롤이
   * 전문을 들고 있어 자동 펼침과 같고, editable=false(읽기 전용 블록 표시)는 호출부
   * (renderWriteNode readonly 분기)가 블록 단위로 접는다.
   */
  collapseLines?: number;
  /**
   * 범용 검색 제안 축(suggestionsFrom — 트리 경로에서만 전달, 행 그리드는 미배선) — 항목 입력 중
   * 서버 검색 드롭다운 제안. 항목별 배선·기추가 항목 제외(excludeTexts=자기 외 항목)는 여기 소유,
   * search closure(catalogKey·filter)와 선택 부속(onPick=writes 전파·usage 통지)은 호출부 소유.
   * 제안 선택 시 그 항목 텍스트를 확정한다(자유 입력은 종전과 동일).
   * requireSelection이면 타이핑은 항목 값이 되지 않고 검색어로만 쓰인다 — 고르지 않고 끝낸
   * 항목은 빈 채로 남으므로 기존 `listAfterEditEnd` 규약대로 목록에서 사라진다(추가 취소).
   * ⚠️ 그 취소 규약은 클릭 진입(entryMode='click')에만 있다 — 상시 입력 목록에는 편집 세션이
   * 없어 빈 항목이 남는다(선택 전용 목록은 클릭 진입과 함께 쓰는 것이 정합).
   */
  suggest?: {
    search: (query: string) => Promise<ComposerSuggestionRow[]>;
    onPick?: (row: ComposerSuggestionRow) => void;
    requireSelection?: boolean;
  };
  /**
   * 선택 전용 목록(suggestionsFrom.requireSelection)인데 **호스트 검색이 미배선**임 — 호출부가
   * 선언(sfList)과 배선(suggest) 유무를 대조해 알려 준다. 이 목록은 값을 만들 방법 자체가 없으므로
   * 항목을 읽기 전용으로 두고 추가를 막는다. 숨기지는 않는다(은닉 금지) — 기존 항목은 그대로 보이고
   * 삭제는 남긴다(잘못 들어간 항목을 걷어낼 수는 있어야 한다) + 왜 못 쓰는지 한 줄로 말한다.
   * ⚠️ 이 판정을 `suggest` 유무로 컴포넌트가 자체 추론할 수는 없다 — 선언은 호출부에만 있다.
   */
  suggestUnavailable?: boolean;
  /**
   * 저작 캔버스에서 렌더 중임(트리 경로에서만 전달 — 행 그리드는 web 전용이라 항상 false).
   * "+ 항목 추가" 후 자동 포커스를 끄는 데만 쓴다: 캔버스는 렌더가 잦고, 포커스가 캔버스로 튀면
   * 속성 패널 조작이 끊긴다(표 위젯 addRow의 suggest.authoringCanvas 판정과 같은 축).
   */
  authoringCanvas?: boolean;
}) {
  const list = normalizeList(value);
  // "+ 항목 추가"(상시 입력 목록) 직후 포커스 조회 범위 — 이 목록 인스턴스.
  // click 진입 목록은 새 항목이 곧바로 편집으로 열리며 focusOnMount가 이미 커서를 준다.
  const listRef = useRef<HTMLDivElement>(null);
  // click 진입 로컬 상태 — 편집 중 항목 인덱스(한 번에 하나) + hover 항목(어포던스 배경은
  // 인라인 state — rc 신규 Tailwind 유틸 금지, hover: variant 포함).
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  // mount 시 1회 포커스 + 커서 끝 — 안정 identity(useCallback)라 타이핑 재렌더에는 재호출되지
  // 않고, 편집 항목 전환(unmount→mount) 시에만 실행된다.
  const focusOnMount = useCallback((el: HTMLTextAreaElement | HTMLInputElement | null) => {
    if (!el) return;
    el.focus();
    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch {
      /* selection 미지원 타입 — 포커스만으로 충분 */
    }
  }, []);
  if (!editable) {
    return (
      <div className={readonlyValueClass} style={readonlyValueStyle}>
        {readonlyText(value)}
      </div>
    );
  }
  const clickEntry = entryMode === 'click';
  /**
   * 클릭 진입 편집 종료 — 내용 없이 끝낸 항목은 목록에서 없앤다(추가를 취소한 것이므로).
   * 판정은 순수 헬퍼(listAfterEditEnd) 소유. `skipDrop`은 같은 필드의 다른 버튼(삭제 ×·추가)으로
   * 포커스가 넘어간 blur — 그 버튼이 자기 조작을 하도록 두고 여기선 인덱스를 건드리지 않는다
   * (동시에 지우면 뒤 항목 인덱스가 밀려 엉뚱한 항목이 삭제된다).
   */
  const endItemEdit = (idx: number, skipDrop = false): void => {
    setEditingIdx(null);
    if (skipDrop) return;
    const next = listAfterEditEnd(list, idx);
    if (next) onChange(next);
  };
  const itemControl = (item: string, idx: number) => {
    const editing = clickEntry && editingIdx === idx;
    // 검색 미배선 + 선택 전용 → 값을 만들 방법이 없는 항목. readOnly 컨트롤로 값만 보여 준다.
    if (suggestUnavailable) {
      const shared = {
        className: cellInputClass,
        style: SUGGEST_UNAVAILABLE_CONTROL_STYLE,
        value: item,
        readOnly: true,
      };
      return multiline ? <textarea rows={2} {...shared} /> : <input type="text" {...shared} />;
    }
    const control = (sctl?: SuggestionControlBindings) => {
      // click 진입 종료(blur/Esc 표시 복귀)와 제안 드롭다운 배선을 합성 — 제안이 이벤트를
      // 소비하면(드롭다운 열림 중 ↑↓/Enter/Esc) 편집 종료 축은 미도달(Esc는 "제안 닫기").
      // suggest·click 둘 다 없으면 핸들러가 전부 no-op이라 always 경로 종전 동작 그대로(회귀 0).
      const shared = {
        className: cellInputClass,
        // 선택 전용 칸은 검색어 초안을 보여준다(항목 값은 제안 선택에서만 바뀐다).
        value: sctl?.draftValue ?? item,
        'aria-autocomplete': sctl ? ('list' as const) : undefined,
        autoComplete: sctl ? 'off' : undefined,
        placeholder: sctl?.requireSelection ? SUGGEST_REQUIRE_SELECTION_PLACEHOLDER : undefined,
        ref: editing ? focusOnMount : undefined,
        onBlur: (e: { relatedTarget?: EventTarget | null }) => {
          sctl?.onBlur();
          if (!editing) return;
          // 같은 필드의 버튼(삭제 ×·항목 추가)으로 포커스가 넘어간 blur는 그 버튼에 조작을 양보.
          const to = e?.relatedTarget;
          const toOwnButton =
            to instanceof HTMLElement && to.closest('[data-list-field-action]') !== null;
          endItemEdit(idx, toOwnButton);
        },
        onKeyDown: (e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          if (sctl?.onKeyDown(e)) return;
          if (editing && e.key === 'Escape') {
            e.preventDefault();
            endItemEdit(idx);
          }
        },
        onChange: (e: { target: { value: string } }) => {
          // 선택 전용 칸: 친 글자는 검색어일 뿐이라 항목 값에 기록하지 않는다.
          // 게이트는 **선언**(suggest.requireSelection)이지 드롭다운 배선(sctl) 유무가 아니다 —
          // sctl이 undefined면 `!sctl?.x`가 true라 타이핑이 그대로 커밋된다.
          if (!isTypedCommitBlocked(suggest)) onChange(updateListItem(list, idx, e.target.value));
          sctl?.onQueryChange(e.target.value);
        },
      };
      return multiline ? <textarea rows={2} {...shared} /> : <input type="text" {...shared} />;
    };
    if (!suggest) return control();
    return (
      <SuggestionDropdown
        search={suggest.search}
        requireSelection={suggest.requireSelection}
        // 기추가 항목 제외 — 자기(편집 중) 외 항목 텍스트를 넘긴다(빈/공백은 드롭다운이 무시).
        excludeTexts={list.filter((_, i) => i !== idx)}
        onPick={(row) => {
          // 값 확정(그 항목 텍스트 교체)은 목록 소유, writes 전파·usage 통지는 호출부(onPick).
          onChange(updateListItem(list, idx, row.text));
          suggest.onPick?.(row);
        }}
      >
        {control}
      </SuggestionDropdown>
    );
  };
  return (
    <div ref={listRef} className="space-y-1.5">
      {list.length === 0 && <p className="text-xs text-gray-400">항목이 없습니다.</p>}
      {list.map((item, idx) => (
        // 항목은 인덱스 위치가 정체성(재정렬 없음) — index key 허용.
        // data-list-item-index: 항목 추가 직후 포커스 앵커(addedRowFocus LIST_ITEM_INDEX_ATTR).
        <div key={idx} data-list-item-index={idx} className="flex items-start gap-1.5">
          {/* 검색 미배선 degrade는 클릭 진입 표시를 쓰지 않는다 — '클릭하여 편집' 어포던스가
              거짓말이 되므로 처음부터 readOnly 컨트롤로 보여 준다. */}
          {!clickEntry || editingIdx === idx || suggestUnavailable ? (
            itemControl(item, idx)
          ) : (
            <div
              role="button"
              tabIndex={0}
              title="클릭하여 편집"
              className="w-full text-sm text-gray-700 whitespace-pre-wrap break-words"
              style={{
                cursor: 'pointer',
                borderRadius: 4,
                padding: '4px 6px',
                backgroundColor: hoverIdx === idx ? 'rgba(15,23,42,0.045)' : undefined,
                transition: 'background-color 120ms',
              }}
              onMouseEnter={() => setHoverIdx(idx)}
              onMouseLeave={() => setHoverIdx((cur) => (cur === idx ? null : cur))}
              // ⚠️ 진입 시 hover를 반드시 끈다 — 이 div는 클릭 즉시 언마운트되므로 onMouseLeave가
              // 영영 오지 않는다. 그대로 두면 편집을 끝내고 돌아왔을 때 마우스가 딴 데 있어도
              // hover 배경이 켜진 채 남는다(이 프로젝트에 기록된 재발 함정).
              onClick={() => {
                setHoverIdx(null);
                setEditingIdx(idx);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setHoverIdx(null);
                  setEditingIdx(idx);
                }
              }}
            >
              {item.trim() === '' ? (
                <span className="text-gray-400 italic">클릭하여 입력</span>
              ) : (
                <CollapsibleText text={item} collapseLines={collapseLines} />
              )}
            </div>
          )}
          <button
            type="button"
            data-list-field-action="remove"
            className="mt-1 text-gray-400 hover:text-red-500 px-1"
            aria-label="항목 삭제"
            onClick={() => {
              // 편집 중 항목이 지워지거나 뒤 항목 인덱스가 밀리면 상태가 어긋나므로 편집 종료로 통일.
              setEditingIdx(null);
              onChange(removeListItem(list, idx));
            }}
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        data-list-field-action="add"
        className="hover:bg-brand-50"
        // 검색 미배선 degrade: 추가해 봐야 채울 수 없는 빈 항목만 쌓이므로 비활성(숨기지는 않는다).
        style={
          suggestUnavailable
            ? { ...addItemButtonStyle, ...SUGGEST_UNAVAILABLE_CONTROL_STYLE }
            : addItemButtonStyle
        }
        disabled={suggestUnavailable}
        onClick={() => {
          // 열려 있던 빈 항목은 먼저 걷어낸다 — 추가는 언제나 "빈 항목 1개"만 만든다.
          const base = clickEntry ? listBeforeAddItem(list, editingIdx) : list;
          // click 진입 모드: 새(빈) 항목을 바로 편집으로 연다.
          if (clickEntry) setEditingIdx(base.length);
          onChange(addListItem(base));
          // 상시 입력 목록(always)에는 편집 세션이 없어 focusOnMount가 걸릴 자리가 없다 —
          // 새 항목 래퍼를 앵커로 잡아 그 안 입력 컨트롤로 커서를 옮긴다(click 모드는 위
          // setEditingIdx→focusOnMount가 이미 처리하므로 건드리지 않는다).
          if (clickEntry || authoringCanvas) return;
          focusAddedRowContainer(listRef.current, LIST_ITEM_INDEX_ATTR, String(base.length));
        }}
      >
        + 항목 추가
      </button>
      {suggestUnavailable && <SuggestNote text={SUGGEST_REQUIRE_SELECTION_UNAVAILABLE} />}
    </div>
  );
}

/**
 * 행 그리드 image/annotated-image 셀 값의 오버레이 축 필터 — 값이 주석 객체
 * (AnnotatedImageValue)일 때, 그 **행**의 규약 오버레이 키를 존중해 숨김 축을 값에서 제거한 뒤
 * 돌려준다. 본문 image 노드의 규칙(RenderTemplate 'image' 분기 — node.overlay 미지정이어도 규약
 * 콘텐츠 키 OVERLAY_SHAPES/STROKES_CONTENT_KEY는 항상 읽는다)과 같은 규칙의 행 스코프판이다:
 * 문서 전역 키 대신 같은 행의 값을 읽으므로, 행에 규약 키 체크박스(showStrokeOverlay 등)를
 * 배치하는 것만으로 그 행 썸네일의 축 표시가 성립한다(체크 토글 → 행 값 변경 → 즉시 반영).
 * 행 필드 선언에는 overlay 저작 옵션이 없어 기본값은 두 축 모두 표시(true) —
 * resolveOverlayAxisVisible 규약대로 undefined/null이면 기본 표시, false/'false'만 숨김.
 * 숨김 축은 값에서 **제거**해 전달한다 — 셀 표시와 클릭 확대(라이트박스)가 같은 값을 받는다.
 */
function applyRowOverlayAxes(row: RowObject, value: unknown): unknown {
  if (!isAnnotatedImageValue(value)) return value;
  const shapesVisible = resolveOverlayAxisVisible(row[OVERLAY_SHAPES_CONTENT_KEY], true);
  const strokesVisible = resolveOverlayAxisVisible(row[OVERLAY_STROKES_CONTENT_KEY], true);
  if (shapesVisible && strokesVisible) return value;
  return {
    url: value.url,
    ...(shapesVisible ? { shapes: value.shapes } : {}),
    ...(strokesVisible ? { strokes: value.strokes } : {}),
  };
}

/** 단일 행 필드 편집 위젯을 field.widget별로 렌더. */
function RowFieldControl({
  field,
  value,
  signerName,
  signatureSlot,
  editable,
  registeredWidgets,
  rowWidgetContext,
  authoringCanvas,
  uploadPhoto,
  onChange,
}: {
  field: DataSourceRowField;
  value: unknown;
  /** widget='signature' 전용 — 그 행에서 읽어온 서명자 표시명(빈 칸 안내에 병기). */
  signerName?: string;
  /**
   * widget='signature' 전용(P2) — resolved 값 + 탭/지우기 콜백(signatureSlotViewProps 산출).
   * 미주입이면 표시 전용 빈 칸(캔버스·미배선 host degrade — 모양 동일).
   */
  signatureSlot?: Pick<
    SignatureSlotViewProps,
    'value' | 'signerName' | 'onRequestSign' | 'onRequestDelete'
  >;
  editable: boolean;
  registeredWidgets?: Record<string, RegisteredRowWidget>;
  rowWidgetContext?: unknown;
  /** 저작 캔버스 렌더 — 사진 클릭 확대만 inert(모양은 동일). 상세는 RowImageField JSDoc. */
  authoringCanvas?: boolean;
  /** widget='photo' 업로드 호스트 콜백 — 미배선이면 값 있는 행은 썸네일, 빈 행은 안내로 degrade. */
  uploadPhoto?: ComposerPhotoUpload;
  onChange: (next: unknown) => void;
}) {
  // 등록형(registered)은 readonly 폴백보다 **먼저** 라우팅 — 등록된 위젯이 항상 렌더하고
  // editable(false 포함)을 계약대로 받아 스스로 표시 전용 모드를 처리한다. 조기 readonly 폴백을
  // 타면 객체 값이 String(value)=[object Object]로 새는 D2 결함이 editable:false 경로에서 재발.
  if (field.widget === 'registered') {
    const Widget = field.widgetKey ? registeredWidgets?.[field.widgetKey] : undefined;
    if (Widget) {
      return (
        <Widget value={value} onChange={onChange} editable={editable} context={rowWidgetContext} />
      );
    }
    // 미등록 위젯(레지스트리 부재): 안내 title + 무해 표시 — 객체 값은 String 대신 '-'
    // ([object Object] 금지, 도메인 위젯 구현은 후속).
    return (
      <div
        className={readonlyValueClass}
        style={readonlyValueStyle}
        title={`등록되지 않은 위젯입니다: ${field.widgetKey ?? '(키 없음)'}`}
      >
        {value !== null && typeof value === 'object' && !Array.isArray(value)
          ? '-'
          : readonlyText(value)}
      </div>
    );
  }

  // image·annotated-image는 registered와 마찬가지로 readonly 폴백보다 **먼저** 라우팅 —
  // 표시 전용(입력 아님)이라 editable 여부와 무관하게 값을 사진으로 표시한다(문자열이면 평면,
  // { url, shapes, strokes } 객체면 도형 오버레이). 값 없으면 회색 placeholder 박스.
  // 표시 크기(displayVariant)와 클릭 확대는 RowImageField 한 벌이 소유한다.
  if (field.widget === 'image' || field.widget === 'annotated-image') {
    return (
      <RowImageField
        widget={field.widget}
        value={value}
        label={field.label}
        displayVariant={field.displayVariant}
        authoringCanvas={authoringCanvas}
      />
    );
  }

  // 사진 업로드(widget='photo')도 readonly 폴백보다 먼저 라우팅 — 값(URL)이 있으면 editable
  // 여부와 무관하게 썸네일이 보여야 한다(텍스트 폴백이면 URL 원문이 샌다). 편집(업로드·삭제)은
  // editable + 호스트 배선(uploadPhoto)일 때만 산다. 컴포넌트는 트리 photo 위젯·DataDefField
  // photo 셀과 같은 한 벌(PhotoUploadWidget compact — 96×64 셀 결).
  if (field.widget === 'photo') {
    const photoAlt = field.label ?? field.field;
    if ((!uploadPhoto && !authoringCanvas) || !editable) {
      const savedUrl = typeof value === 'string' ? value.trim() : '';
      return savedUrl !== '' ? (
        <ImageWithFallback src={savedUrl} alt={photoAlt} style={PHOTO_CELL_THUMB_STYLE} />
      ) : (
        <p className="text-xs text-gray-400">
          {editable ? '사진 업로드는 내부 작성 화면에서 할 수 있습니다.' : '—'}
        </p>
      );
    }
    return (
      <PhotoUploadWidget
        value={value}
        alt={photoAlt}
        onValueChange={(next) => onChange(next)}
        uploadPhoto={uploadPhoto}
        authoringCanvas={authoringCanvas}
        compact
      />
    );
  }

  // 체크박스(행 토글) — readonly 폴백보다 먼저 라우팅: 값 미정의(구 데이터)는 선언 defaultValue로
  // 보이고(규약 — shared 'checkbox' JSDoc), editable=false면 비활성 표시(값 텍스트 폴백이면
  // 'true'가 샌다). 사용자 조작은 명시 boolean을 기록한다.
  if (field.widget === 'checkbox') {
    const checked =
      value === undefined || value === null || value === ''
        ? Boolean(field.defaultValue)
        : !(value === false || value === 'false');
    return (
      <label
        className="inline-flex items-center gap-2 text-sm text-gray-700"
        style={{ cursor: editable ? 'pointer' : 'default' }}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={!editable}
          onChange={(e) => onChange(e.target.checked)}
        />
        {/* 라벨은 체크박스 **뒤 인라인** 한 줄이다(`[☑] 라벨`) — 이 위젯에 한해 그리드 상단
            라벨을 억제한다(rows.map 렌더부의 checkbox 분기). 보이는 텍스트가 곧 <label>의
            접근성 이름이라 별도 aria-label을 달지 않는다. */}
        <span>{field.label ?? field.field}</span>
      </label>
    );
  }

  // 서명 슬롯도 image·annotated-image와 같은 이유로 readonly 폴백보다 먼저 라우팅한다. 표시 전용
  // (값을 텍스트로 접으면 서명 data URL이 통째로 새 나온다)이고, editable 여부와 무관하게 같은
  // 서명 칸을 보여야 한다 — 서명은 이 칸에 타이핑해서 넣는 값이 아니라 별도로 걷어 오는 증빙이다.
  // P2: 값·상호작용은 resolved 채널(signatureSlot — content 행 값(value)은 읽지 않는다 §3-2).
  if (field.widget === 'signature') {
    return signatureSlot ? (
      <SignatureSlotView {...signatureSlot} />
    ) : (
      <SignatureSlotView value={undefined} signerName={signerName} />
    );
  }

  // editable:false → (등록형 외) 위젯 종류와 무관하게 읽기 전용 표시.
  if (!editable || field.widget === 'readonly') {
    return (
      <div className={readonlyValueClass} style={readonlyValueStyle}>
        {readonlyFieldText(field, value)}
      </div>
    );
  }

  switch (field.widget) {
    // 날짜/기간은 작성폼 트리 위젯과 같은 컨트롤을 쓴다(달력 진입점·값 채널 단일 정본).
    // 컨트롤 결만 이 그리드의 셀 입력 결(cellInputClass)로 준다 — 형제 칸과 테두리·패딩이 갈리면
    // 같은 카드 안에서 날짜 칸만 튄다.
    case 'date':
      return (
        <DateFieldInput
          value={value}
          className={cellInputClass}
          onChange={(next) => onChange(next)}
        />
      );

    case 'date-range':
      return (
        <DateRangeFieldInput
          value={value}
          className={cellInputClass}
          onChange={(next) => onChange(next)}
        />
      );

    case 'textarea':
      return (
        <textarea
          className={cellInputClass}
          rows={3}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'textarea-list':
      return <TextareaListField value={value} editable={editable} onChange={(next) => onChange(next)} />;

    case 'text-list':
      return (
        <TextareaListField
          value={value}
          editable={editable}
          multiline={false}
          onChange={(next) => onChange(next)}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          className={cellInputClass}
          value={value === undefined || value === null || value === '' ? '' : (value as number)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
      );

    case 'text':
    default:
      return (
        <input
          type="text"
          className={cellInputClass}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

export default function RowsCollectionEditor({
  node,
  value,
  seed,
  onChange,
  registeredWidgets,
  rowWidgetContext,
  signatureSlots,
  readOnly = false,
  authoringCanvas = false,
  uploadPhoto,
}: RowsCollectionEditorProps) {
  const rowFields = node.fields;
  // 표시 대상 필드 — hidden(행 id/조인 키 등 내부 식별)은 컬럼·요약에서 제외(데이터에는 유지,
  // 행 추가(addRow) 초기화는 전체 rowFields 기준이라 무영향).
  const visibleFields = rowFields.filter((f) => !f.hidden);
  const rows = normalizeRows(value);
  // 추가/삭제는 별개 축(shared effectiveRowAddRemove — allowAddRemove 상위 스위치 + allowAdd/allowRemove).
  // readOnly면 둘 다 없음.
  const addRemove = effectiveRowAddRemove(node);
  const allowAdd = !readOnly && addRemove.add;
  const allowRemove = !readOnly && addRemove.remove;
  // rowLimit 도달 시 추가 버튼 비활성(삭제는 게이트하지 않는다 — DataSourceDef.rowLimit JSDoc).
  const addBlocked = isRowAddBlockedByLimit(node.rowLimit, rows.length);

  // 행별 접힘 상태(로컬 UI, 저장 안 함) — rowId 축이라 삭제/재정렬에 불변.
  // 새로 추가한 행은 Set에 없으므로 자동 펼침, 기존 접힘은 유지된다.
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(() => new Set());
  // 행 추가 포커스 조회 범위 — 한 작성 화면에 그리드가 여럿일 수 있어 document 전역 조회는 금물.
  const gridRef = useRef<HTMLDivElement | null>(null);

  // 요약 헤더에 쓸 텍스트 필드(문자열 위젯) 처음 두 개 — 행 무관 선정. 행별 `visibleWhen`은 행
  // 루프 안에서 한 번 더 거른다(rowVisibleFields — 펼침 편집 영역과 같은 술어). 여기서 걸러 두면
  // 숨긴 칸이 요약에 "(라벨 미입력)"으로 남아 그 행에 없는 칸을 빈 것처럼 보인다(2026-09-14).
  const summaryFields = summaryFieldsForRow(rowFields);
  const isAllCollapsed = isAllRowsCollapsed(rows, collapsedKeys);

  const toggleRow = (key: string) => setCollapsedKeys((prev) => toggleCollapseKey(prev, key));

  const toggleAll = () => setCollapsedKeys(collapsedForToggleAll(rows, isAllCollapsed));

  // 행 스코프 쓰기 누적 = 함수형 갱신의 prev(반복 행 RepeatRowsBlock과 같은 규약 — RowScopedPending
  // JSDoc). 등록형 위젯이 비동기로 시차를 두고 커밋하면(improvement-image 자동 생성, 행 N개 병렬)
  // 렌더 시점 `rows` 스냅샷에서 출발한 커밋이 직전 커밋을 덮어 유실한다. 모든 쓰기(필드 갱신·되돌리기·
  // 행 추가/삭제)가 `baseRows()`에서 출발해 `commitRows`로 올려보내고, 매 렌더 후 호스트가 돌려준 `value`가
  // 우리 배열이면 pending 유지, 외부 변경이면 폐기한다. 비교 대상은 rows가 아니라 `value` 원본
  // (normalizeRows는 배열 identity를 보존한다).
  const rowsPending = useRef<RowScopedPending>({ current: null, sent: new WeakSet() }).current;
  useEffect(() => {
    reconcileRowScopedPending(rowsPending, value);
  });
  const baseRows = (): RowObject[] => rowsPending.current ?? rows;
  const commitRows = (next: RowObject[]) => {
    commitRowScopedRows(rowsPending, next);
    onChange(next);
  };

  const handleDeleteRow = (rowId: string | undefined, key: string) => {
    commitRows(removeRow(baseRows(), rowId));
    setCollapsedKeys((prev) => collapsedAfterRemove(prev, key));
  };

  if (rowFields.length === 0) {
    return (
      <p className="text-xs text-gray-400 px-3 py-2 border border-dashed border-gray-300 rounded-card">
        행 필드 정의가 없어 편집할 수 없습니다.
      </p>
    );
  }

  return (
    <div className="space-y-3" ref={gridRef}>
      {rows.length === 0 && (
        <p className="text-xs text-gray-400 px-3 py-3 border border-dashed border-gray-300 rounded-card text-center">
          행이 없습니다.{allowAdd ? ' 아래 버튼으로 추가하세요.' : ''}
        </p>
      )}

      {/* 모두 접기/펴기 — 전부 접힘이면 '모두 펴기', 아니면 '모두 접기'. */}
      {rows.length > 0 && (
        <div className="flex items-center justify-end">
          <button
            type="button"
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-control hover:bg-gray-50"
            onClick={toggleAll}
          >
            {isAllCollapsed ? '모두 펴기' : '모두 접기'}
          </button>
        </div>
      )}

      {rows.map((row, rowIdx) => {
        const seedRow = findSeedRow(seed, row.rowId);
        const collapseKey = rowCollapseKey(row, rowIdx);
        const isExpanded = !collapsedKeys.has(collapseKey);
        // 요약 헤더도 행별 visibleWhen을 탄다(펼침 편집 영역과 같은 술어) — 그 행에서 숨긴 칸은
        // 요약에서도 빠진다(남기면 "(라벨 미입력)"으로 없는 칸이 빈 것처럼 보인다).
        const rowSummaryFields = rowVisibleFields(summaryFields, row);
        return (
          <div
            key={row.rowId ?? rowIdx}
            // 행 앵커 — 추가 직후 그 행의 첫 입력 칸을 찾는 데 쓴다(addedRowFocus).
            data-row-card-id={collapseKey}
            className="border border-gray-200 rounded-card"
            style={rowCardStyle}
          >
            {/* 요약 헤더(클릭=토글) — 접힘 시 헤더만, 펼침 시 헤더(border-b)+편집 영역. */}
            <div
              className="flex items-center gap-2 px-3 py-2"
              style={{
                ...rowHeaderBaseStyle,
                borderBottom: isExpanded ? '1px solid #e2e8f0' : undefined,
              }}
              onClick={() => toggleRow(collapseKey)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleRow(collapseKey);
                }
              }}
              aria-expanded={isExpanded}
            >
              <span className="text-xs font-semibold" style={rowBadgeStyle}>
                {rowIdx + 1}
              </span>
              {rowSummaryFields.length > 0 ? (
                rowSummaryFields.map((field, i) => (
                  <span
                    key={field.field}
                    className={i === 0 ? 'text-sm font-medium' : 'text-sm text-gray-600'}
                    style={i === 0 ? summaryPrimaryStyle : summarySecondaryStyle}
                  >
                    {summaryText(row, field)}
                  </span>
                ))
              ) : (
                <span style={{ flex: '1 1 0%' }} />
              )}
              {allowRemove && (
                <button
                  type="button"
                  className="text-gray-400 hover:text-red-500 text-sm px-1 flex-shrink-0"
                  aria-label="행 삭제"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteRow(row.rowId, collapseKey);
                  }}
                >
                  × 행 삭제
                </button>
              )}
              <span className="text-gray-400 flex-shrink-0" aria-hidden="true">
                {isExpanded ? '▲' : '▼'}
              </span>
            </div>

            {/* 펼침 편집 영역 — hidden 필드(내부 식별) 제외 + 행별 visibleWhen 필터(행 상태에
                따라 갈리는 칸 — 같은 field 두 선언의 상보 조건 분기 포함). key는 field+색인:
                상보 조건이 어긋나(저작 실수) 같은 field 두 선언이 한 행에 동시 렌더돼도
                React key 충돌로 한쪽이 조용히 삼켜지지 않게 한다(둘 다 보여야 실수가 보인다). */}
            {isExpanded && (
              <div className="p-3" style={rowBodyStyle}>
                {rowVisibleFields(visibleFields, row).map((field, fieldIdx) => {
                  const editable = !readOnly && field.editable !== false;
                  const fieldValue = row[field.field];
                  const modified = editable && isFieldModified(fieldValue, seedRow, field.field);
                  // image 계열 셀 표시값 — 행 규약 오버레이 키로 숨김 축을 제거해 넘긴다
                  // (applyRowOverlayAxes doc). modified(되돌리기) 대조는 원시 값(fieldValue)
                  // 기준 — 표시 필터가 seed 대조를 오염시키지 않는다.
                  const displayValue =
                    field.widget === 'image' || field.widget === 'annotated-image'
                      ? applyRowOverlayAxes(row, fieldValue)
                      : fieldValue;
                  // checkbox는 상단 라벨을 억제한다 — 라벨은 RowFieldControl이 체크박스 뒤
                  // 인라인으로 그린다(`[☑] 라벨` 한 줄). 되돌리기 버튼이 필요한 때만 헤더 행 유지.
                  const inlineLabel = field.widget === 'checkbox';
                  return (
                    // 칸 앵커 — 행 앵커와 짝으로 "방금 만든 행의 첫 입력 칸"을 특정한다(addedRowFocus).
                    <div key={`${field.field}:${fieldIdx}`} data-row-field={field.field}>
                      {(!inlineLabel || (modified && seedRow)) && (
                        <div className="flex items-center gap-2 mb-1">
                          {!inlineLabel && (
                            <label className={fieldLabelClass + ' mb-0'}>{field.label ?? field.field}</label>
                          )}
                          {modified && seedRow && (
                            <RevertButton
                              onClick={() =>
                                commitRows(
                                  updateRowField(baseRows(), row.rowId, field.field, seedRow[field.field]),
                                )
                              }
                            />
                          )}
                        </div>
                      )}
                      <RowFieldControl
                        field={field}
                        value={displayValue}
                        signerName={rowSignerName(row, field)}
                        // 서명 슬롯 resolved 값+상호작용(P2) — 주소는 def.key(=node.bindingKey)와
                        // 필드 선언에서 해소(3채널 공용 signatureSlotViewProps 단일 지점).
                        signatureSlot={
                          field.widget === 'signature'
                            ? signatureSlotViewProps(signatureSlots, node.bindingKey, field, row)
                            : undefined
                        }
                        editable={editable}
                        registeredWidgets={registeredWidgets}
                        // 등록형 위젯에 이 행 객체(scopeValues)를 함께 싣는다 — 이웃 필드를 읽는
                        // 위젯(improvement-image)의 채널. 브리지(rowWidgetAsFormWidget)와 같은 키.
                        rowWidgetContext={
                          field.widget === 'registered'
                            ? { ...(rowWidgetContext as Record<string, unknown> | undefined), scopeValues: row }
                            : rowWidgetContext
                        }
                        authoringCanvas={authoringCanvas}
                        uploadPhoto={uploadPhoto}
                        onChange={(next) =>
                          commitRows(updateRowField(baseRows(), row.rowId, field.field, next))
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {allowAdd && (
        <button
          type="button"
          className="hover:bg-brand-50"
          style={{ ...addItemButtonStyle, ...(addBlocked ? addItemButtonDisabledStyle : {}) }}
          disabled={addBlocked}
          title={rowAddLimitTitle(node.rowLimit, rows.length)}
          onClick={() => {
            const next = addRow(baseRows(), rowFields);
            commitRows(next);
            // 방금 만든 행의 첫 입력 칸으로 커서 — 표 위젯·항목 목록과 같은 규약(빈 행만 덩그러니
            // 생기고 어디를 눌러야 할지 모르는 상태를 만들지 않는다). 새 행은 collapsedKeys에 없어
            // 항상 펼침이라 그 칸이 실제로 렌더돼 있다.
            const added = next[next.length - 1];
            // unavailable(선택 전용+검색 미배선 degrade)은 표 경로에만 있는 축 — 행 그리드 필드
            // (DataSourceRowField)에는 suggestionsFrom 자체가 없어 그 상태가 생기지 않는다(생략 정확).
            const focusField = firstTypeableCellKey(
              visibleFields.map((f) => ({
                key: f.field,
                widget: f.widget,
                locked: readOnly || f.editable === false,
              })),
            );
            if (added?.rowId && focusField) {
              focusAddedGridField(gridRef.current, added.rowId, focusField);
            }
          }}
        >
          + 행 추가
        </button>
      )}
    </div>
  );
}

/**
 * 편집형 list 데이터셋 1개의 "라벨 + 행 그리드" 필드 블록.
 * web 실폼(ComposerWidgetTreeRenderer)과 admin 캔버스 라이브 모드가 **같은 컴포넌트**를 주입해
 * 렌더 결을 구조적으로 일치시킨다(캔버스↔실폼 차이는 전부 결함 — WYSIWYG 원칙).
 */
export function DataSourceRowsField({
  def,
  value,
  seed,
  registeredWidgets,
  rowWidgetContext,
  signatureSlots,
  onChange,
  readOnly,
  authoringCanvas,
  uploadPhoto,
}: {
  def: DataSourceDef;
  /** 편집본 행 배열(=values[def.key]). */
  value: unknown;
  /** seed 원본 행 배열(=values[def.key + ROWS_SEED_SUFFIX]) — 되돌리기 대조. */
  seed: unknown;
  registeredWidgets?: Record<string, RegisteredRowWidget>;
  /** host 작성 화면 컨텍스트(예 { values, resolvedRows }) — registered 위젯 context로 전달. */
  rowWidgetContext?: unknown;
  /** 서명 슬롯 host 채널(P2) — RowsCollectionEditor로 통과. */
  signatureSlots?: ComposerSignatureSlotsContext;
  onChange: (nextRows: RowObject[]) => void;
  readOnly?: boolean;
  /** admin 저작 캔버스 렌더 — RowsCollectionEditor로 통과(사진 클릭 확대만 inert). */
  authoringCanvas?: boolean;
  /** widget='photo' 업로드 호스트 콜백 — RowsCollectionEditor로 통과. */
  uploadPhoto?: ComposerPhotoUpload;
}) {
  const dsNode = dataSourceToRowsNode(def);
  return (
    <div>
      <label className={fieldLabelClass}>{dsNode.label}</label>
      <RowsCollectionEditor
        node={dsNode}
        value={value}
        seed={seed}
        registeredWidgets={registeredWidgets}
        rowWidgetContext={rowWidgetContext}
        signatureSlots={signatureSlots}
        onChange={onChange}
        readOnly={readOnly}
        authoringCanvas={authoringCanvas}
        uploadPhoto={uploadPhoto}
      />
    </div>
  );
}
